import React, { useState } from "react";
import { Dialog, Box } from "@mui/material";
import { LocationOn as LocationIcon } from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";

interface LocationConflictDialogProps {
  open: boolean;
  machineName: string;
  currentLocation: string;
  machineLocation: string;
  onKeepCurrent: () => void;
  onUpdateLocation: () => void;
}

const AMBER = "#E8A817";

const LocationConflictDialog = ({
  open,
  machineName,
  currentLocation,
  machineLocation,
  onKeepCurrent,
  onUpdateLocation,
}: LocationConflictDialogProps) => {
  const [hoveredCard, setHoveredCard] = useState<"keep" | "update" | null>(null);

  return (
    <Dialog
      open={open}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          maxWidth: "560px",
          width: "90vw",
          boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
          animation: "dialogSlideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          "@keyframes dialogSlideUp": {
            from: { opacity: 0, transform: "translateY(12px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        },
      }}
    >
      {/* Amber gradient accent bar */}
      <Box
        sx={{
          height: "4px",
          background: `linear-gradient(90deg, ${AMBER}, #F5C842)`,
        }}
      />

      {/* Content */}
      <Box sx={{ padding: "28px 32px 32px" }}>
        {/* Header: icon + title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
          <Box
            sx={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              backgroundColor: "rgba(232, 168, 23, 0.10)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <LocationIcon sx={{ color: AMBER, fontSize: "22px" }} />
          </Box>
          <Box
            component="h2"
            sx={{
              margin: 0,
              fontFamily: dt.font,
              fontSize: "22px",
              fontWeight: 700,
              color: "#1A1A2E",
              letterSpacing: "-0.3px",
            }}
          >
            Location Conflict
          </Box>
        </Box>

        {/* Subtitle */}
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "14px",
            lineHeight: "22px",
            color: "#6B7280",
            marginBottom: "24px",
          }}
        >
          Assigning <strong style={{ color: "#1A1A2E", fontWeight: 600 }}>{machineName}</strong> — this machine is at a different location than the work order.
        </Box>

        {/* Two side-by-side cards */}
        <Box sx={{ display: "flex", gap: "16px" }}>
          {/* Left card — Keep current location (secondary) */}
          <Box
            onClick={onKeepCurrent}
            onMouseEnter={() => setHoveredCard("keep")}
            onMouseLeave={() => setHoveredCard(null)}
            sx={{
              flex: 1,
              border: `1.5px solid #E8E5DF`,
              borderRadius: "12px",
              backgroundColor: hoveredCard === "keep" ? "#F4F5F7" : "#FAFBFC",
              padding: "20px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              transform: hoveredCard === "keep" ? "translateY(-2px)" : "none",
              boxShadow: hoveredCard === "keep"
                ? "0 4px 16px rgba(0,0,0,0.08)"
                : "0 1px 3px rgba(0,0,0,0.03)",
            }}
          >
            {/* Card title */}
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "11px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                color: "#8C9099",
                marginBottom: "12px",
              }}
            >
              Keep Current Location
            </Box>

            {/* Location row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <LocationIcon sx={{ color: "#9CA3AF", fontSize: "18px", flexShrink: 0 }} />
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#1A1A2E",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {currentLocation}
              </Box>
            </Box>

            {/* Helper text */}
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                color: "#9CA3AF",
                lineHeight: "17px",
              }}
            >
              Work order stays at its current location
            </Box>
          </Box>

          {/* Right card — Use machine's location (primary/amber) */}
          <Box
            onClick={onUpdateLocation}
            onMouseEnter={() => setHoveredCard("update")}
            onMouseLeave={() => setHoveredCard(null)}
            sx={{
              flex: 1,
              border: `1.5px solid ${AMBER}`,
              borderRadius: "12px",
              backgroundColor: hoveredCard === "update"
                ? "rgba(232, 168, 23, 0.08)"
                : "rgba(232, 168, 23, 0.04)",
              padding: "20px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              transform: hoveredCard === "update" ? "translateY(-2px)" : "none",
              boxShadow: hoveredCard === "update"
                ? `0 4px 16px rgba(232, 168, 23, 0.20)`
                : "0 1px 3px rgba(0,0,0,0.03)",
            }}
          >
            {/* Card title */}
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "11px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                color: AMBER,
                marginBottom: "12px",
              }}
            >
              Use Machine's Location
            </Box>

            {/* Location row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <LocationIcon sx={{ color: AMBER, fontSize: "18px", flexShrink: 0 }} />
              <Box
                sx={{
                  fontFamily: dt.font,
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#1A1A2E",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {machineLocation}
              </Box>
            </Box>

            {/* Helper text */}
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "12px",
                color: "#9CA3AF",
                lineHeight: "17px",
              }}
            >
              Update work order to match the machine
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default LocationConflictDialog;
